/**
 * Блок «Дистрибутивы» внутри KB-sidebar (по умолчанию свёрнут).
 *
 * - GET /api/v1/downloads → секции+файлы из BC (через backend proxy).
 * - «Скачать»: прямая публичная BC-ссылка.
 * - «Клиенту»: SendDistroModal → POST /api/v1/downloads/share. По логике
 *   аналогично KBShareModal: выбираем организацию (с поиском по имени/ИНН),
 *   опционально тикет внутри неё, шлём в чат тикета + push в TG/MAX
 *   (если каналы привязаны к организации). Email-уведомление assignee
 *   уходит автоматически как при обычном ответе агента.
 */
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Download, Loader2, Share2, Send, X, Loader } from 'lucide-react';
import { api } from '../api/client';
import { orgsApi, type Organization } from '../api/organizations';
import { ticketsApi, type TicketBrief } from '../api/tickets';

interface DistroFile {
  id: number;
  filename: string;
  description: string | null;
  url: string;
  updated_at: string | null;
}
interface DistroSectionT {
  id: number;
  name: string;
  description: string | null;
  files: DistroFile[];
}

// v2 — старый ключ kb.distros.collapsed мог быть сохранён с true; чтобы
// перевод «свёрнут по умолчанию» сработал у существующих пользователей,
// меняем имя ключа.
const COLLAPSED_KEY = 'kb.distros.open.v2';

interface Props { isStaff: boolean; }

export function DistrosSection({ isStaff }: Props) {
  const [sections, setSections] = useState<DistroSectionT[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSED_KEY) || 'false'); } catch { return false; }
  });
  const [openSecs, setOpenSecs] = useState<Record<number, boolean>>({});
  const [sending, setSending] = useState<{ file: DistroFile; sectionName: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get<DistroSectionT[]>('/api/v1/downloads');
        setSections(data);
      } catch (e: any) {
        setErr(e?.response?.data?.detail || e?.message || 'Не удалось загрузить');
      } finally { setLoading(false); }
    })();
  }, []);

  const toggleTop = () => setOpen(v => {
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(!v)); return !v;
  });
  const toggleSec = (id: number) => setOpenSecs(s => ({ ...s, [id]: !s[id] }));

  const totalFiles = sections.reduce((n, s) => n + s.files.length, 0);

  return (
    <div className="border-b border-slate-800">
      <button onClick={toggleTop}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-800/40 sticky top-0 bg-slate-900 border-b border-slate-800 z-10">
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown size={12} className="text-slate-500" /> : <ChevronRight size={12} className="text-slate-500" />}
          <Download size={11} className="text-blue-400" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-300">Дистрибутивы</span>
        </span>
        {!loading && !err && (
          <span className="text-[10px] text-slate-500 font-mono">{totalFiles}</span>
        )}
      </button>

      {open && (
        loading ? (
          <div className="p-3 flex justify-center"><Loader2 size={14} className="animate-spin text-blue-400" /></div>
        ) : err ? (
          <div className="p-3 text-[11px] text-red-400">{err}</div>
        ) : sections.length === 0 ? (
          <div className="p-3 text-[11px] text-slate-500 text-center">Сборок нет.</div>
        ) : (
          <ul>
            {sections.map(s => {
              const secOpen = openSecs[s.id];
              return (
                <li key={s.id} className="border-b border-slate-800/60 last:border-b-0">
                  <button onClick={() => toggleSec(s.id)}
                    className="w-full px-3 py-1.5 flex items-center justify-between text-left hover:bg-slate-800/40">
                    <span className="flex items-center gap-1.5">
                      {secOpen ? <ChevronDown size={10} className="text-slate-600" /> : <ChevronRight size={10} className="text-slate-600" />}
                      <span className="text-[11px] text-slate-300 truncate">{s.name}</span>
                    </span>
                    <span className="text-[10px] text-slate-600 font-mono">{s.files.length}</span>
                  </button>
                  {secOpen && (
                    <ul className="bg-slate-950/40">
                      {s.files.map(f => (
                        <li key={f.id} className="px-3 py-2 border-t border-slate-800/40">
                          <div className="text-[12px] text-white truncate" title={f.filename}>{f.filename}</div>
                          {f.description && (
                            <div className="text-[10px] text-slate-500 truncate" title={f.description}>{f.description}</div>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            {f.url ? (
                              <a href={f.url} target="_blank" rel="noopener noreferrer"
                                 className="inline-flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300">
                                <Download size={10} /> Скачать
                              </a>
                            ) : (
                              <span className="text-[10px] text-slate-600">ссылки нет</span>
                            )}
                            {isStaff && f.url && (
                              <button onClick={() => setSending({ file: f, sectionName: s.name })}
                                className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-blue-400"
                                title="Отправить клиенту в чат тикета или в чаты организации">
                                <Share2 size={10} /> Клиенту
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )
      )}

      {sending && (
        <SendDistroModal file={sending.file} sectionName={sending.sectionName}
          onClose={() => setSending(null)} />
      )}
    </div>
  );
}


/**
 * Модалка отправки дистрибутива. Логика как у KBShareModal:
 * orgs picker (поиск по имени + ИНН) → опциональный тикет в этой орг
 * → POST /api/v1/downloads/share (добавит Message в тикет + push в TG/MAX).
 */
function SendDistroModal({ file, sectionName, onClose }:
  { file: DistroFile; sectionName: string; onClose: () => void }) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgQ, setOrgQ] = useState('');
  const [orgId, setOrgId] = useState<string>('');
  const [tickets, setTickets] = useState<TicketBrief[]>([]);
  const [ticketId, setTicketId] = useState<string>('');
  const [alsoChannels, setAlsoChannels] = useState(true);
  const [internal, setInternal] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => { orgsApi.list().then(setOrgs).catch(() => {}); }, []);

  useEffect(() => {
    if (!orgId) { setTickets([]); setTicketId(''); return; }
    ticketsApi.list({ organization_id: orgId, limit: 50 }).then(r => {
      const openOnes = r.items.filter(t => !['closed','resolved','cancelled'].includes(t.status));
      setTickets(openOnes);
      setTicketId(openOnes[0]?.id || '');
    }).catch(() => { setTickets([]); setTicketId(''); });
  }, [orgId]);

  const filteredOrgs = useMemo(() => {
    const s = orgQ.trim().toLowerCase();
    if (!s) return orgs.slice(0, 30);
    return orgs.filter(o =>
      o.name.toLowerCase().includes(s) || (o.inn || '').includes(s),
    ).slice(0, 30);
  }, [orgs, orgQ]);

  const orgName = orgs.find(o => o.id === orgId)?.name || '';

  const submit = async () => {
    if (!orgId) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post('/api/v1/downloads/share', {
        organization_id: orgId,
        ticket_id: ticketId || null,
        file_url: file.url,
        file_name: file.filename,
        section_name: sectionName,
        message: message.trim() || null,
        also_channels: alsoChannels,
        internal,
        // TG/MAX боты иногда отвечают медленно (3-10с каждый), глобальный
        // axios timeout 15s слишком жёсткий → даём 60с конкретно этому
        // вызову, чтобы UI не вешал ошибку при успешной отправке.
      }, { timeout: 60_000 });
      const chans: string[] = [];
      if (data.ticket_number) {
        const n = String(data.ticket_number).padStart(4, '0');
        chans.push(`веб-чат SD-${n}`);
      }
      if (data.sent_to_tg) chans.push('Telegram');
      if (data.sent_to_max) chans.push('MAX');
      const dest = chans.length ? chans.join(' + ') : '(не отправлено)';
      const note = data.notes?.length ? ` · ${data.notes.join('; ')}` : '';
      setResult({ ok: true, text: `Отправлено в «${orgName}»: ${dest}${note}` });
    } catch (e: any) {
      setResult({ ok: false, text: e?.response?.data?.detail || e?.message || 'Ошибка' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl
                      max-h-[calc(100vh-2rem)] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between items-start shrink-0">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-white">Отправить клиенту</h3>
            <p className="text-[11px] text-slate-500 mt-0.5 truncate">
              {sectionName} · <span className="text-slate-400">{file.filename}</span>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white shrink-0 ml-2">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-3 overflow-y-auto">
          {!orgId ? (
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Организация</span>
              <input value={orgQ} onChange={e => setOrgQ(e.target.value)} autoFocus
                placeholder="Поиск по имени или ИНН…"
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
              <ul className="max-h-56 overflow-y-auto mt-1 border border-slate-800 rounded">
                {filteredOrgs.length === 0 ? (
                  <li className="px-2 py-3 text-xs text-slate-500 text-center">Никого не нашлось.</li>
                ) : filteredOrgs.map(o => (
                  <li key={o.id}
                    onClick={() => setOrgId(o.id)}
                    className="px-2 py-1.5 cursor-pointer hover:bg-slate-800/70 border-b border-slate-800/60 last:border-b-0">
                    <div className="text-sm text-white truncate">{o.name}</div>
                    {o.inn && <div className="text-[10px] text-slate-500 font-mono">ИНН {o.inn}</div>}
                  </li>
                ))}
              </ul>
            </label>
          ) : (
            <>
              <div className="bg-slate-950 border border-slate-700 rounded px-3 py-2 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{orgName}</div>
                </div>
                <button onClick={() => { setOrgId(''); setTicketId(''); }}
                        className="text-slate-500 hover:text-white text-xs">
                  Сменить
                </button>
              </div>

              <label className="block">
                <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Тикет</span>
                <select value={ticketId} onChange={e => setTicketId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
                  <option value="">— без тикета (в чаты организации)</option>
                  {tickets.map(t => (
                    <option key={t.id} value={t.id}>
                      SD-{String(t.number).padStart(4, '0')} · {t.subject}
                    </option>
                  ))}
                </select>
                {tickets.length === 0 && (
                  <p className="text-[10px] text-slate-500 mt-1">
                    Активных тикетов нет — отправим в TG/MAX-чаты организации (если привязаны).
                  </p>
                )}
              </label>

              <div className="flex items-center gap-4 text-xs">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={alsoChannels}
                    onChange={e => setAlsoChannels(e.target.checked)} className="accent-blue-600" />
                  <span className="text-slate-300">Дублировать в TG/MAX-чат</span>
                </label>
                {ticketId && (
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={internal}
                      onChange={e => setInternal(e.target.checked)} className="accent-blue-600" />
                    <span className="text-slate-300">Внутренний (клиент не увидит)</span>
                  </label>
                )}
              </div>

              <label className="block">
                <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">
                  Сообщение (опционально)
                </span>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3}
                  placeholder="Напр.: «Свежая сборка под Astra 1.8.4, README внутри»"
                  className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
              </label>
            </>
          )}

          {result && (
            <div className={`text-xs px-3 py-2 rounded ${
              result.ok ? 'bg-emerald-900/30 border border-emerald-800 text-emerald-300'
                        : 'bg-red-900/30 border border-red-800 text-red-300'
            }`}>{result.text}</div>
          )}
        </div>

        <footer className="p-4 border-t border-slate-800 flex justify-end gap-2 shrink-0">
          <button onClick={onClose} className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded text-sm">
            Закрыть
          </button>
          <button onClick={submit} disabled={!orgId || busy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded text-sm font-bold inline-flex items-center gap-1.5">
            {busy ? <Loader size={13} className="animate-spin" /> : <Send size={13} />}
            Отправить
          </button>
        </footer>
      </div>
    </div>
  );
}
